import { useNavigate } from "react-router-dom";
import {
  ArrowUUpLeft, ArrowUUpRight, MagnifyingGlassMinus, MagnifyingGlassPlus,
  DownloadSimple, ShareNetwork, PenNib, House, Check, FilePng, FileJpg, FilePdf, Link, Lightning, ArrowsOut, FloppyDisk, MagnifyingGlass, Crown, FilmSlate, Images, Lock,
} from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../ui/dropdown-menu";

export default function TopBar({
  title, onTitleChange, canUndo, canRedo, onUndo, onRedo,
  zoom, onZoomIn, onZoomOut, onExport, onShare, saving,
  performanceMode, onTogglePerformance, onResizeClick, onSaveTemplate, onFindReplace,
  isPro, onUpgrade, onExportVideo, isAdmin, onSaveToStyles,
}) {
  const navigate = useNavigate();
  return (
    <header className="h-14 border-b border-white/10 flex items-center px-3 justify-between bg-[#121212] z-50 shrink-0">
      <div className="flex items-center gap-2 min-w-0">
        <button onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard" className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors shrink-0">
          <House weight="fill" size={18} className="text-zinc-300" />
        </button>
        <div className="flex items-center gap-1.5 shrink-0">
          <div className="h-7 w-7 rounded-md bg-blue-600 flex items-center justify-center">
            <PenNib weight="fill" size={15} className="text-white" />
          </div>
        </div>
        <input
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          data-testid="design-title-input"
          className="bg-transparent text-sm font-medium px-2 py-1 rounded hover:bg-white/5 focus:bg-white/10 outline-none min-w-0 w-40 sm:w-56 truncate"
        />
        <span className="text-xs text-zinc-500 hidden sm:block">{saving ? "Saving…" : "Saved"}</span>
      </div>

      <div className="flex items-center gap-1">
        <button onClick={onUndo} disabled={!canUndo} data-testid="undo-btn" className="h-9 w-9 rounded-lg hover:bg-white/10 disabled:opacity-30 flex items-center justify-center transition-colors">
          <ArrowUUpLeft size={18} />
        </button>
        <button onClick={onRedo} disabled={!canRedo} data-testid="redo-btn" className="h-9 w-9 rounded-lg hover:bg-white/10 disabled:opacity-30 flex items-center justify-center transition-colors">
          <ArrowUUpRight size={18} />
        </button>
        <div className="w-px h-5 bg-white/10 mx-1 hidden sm:block" />
        <div className="hidden sm:flex items-center gap-1">
          <button onClick={onZoomOut} data-testid="zoom-out-btn" className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors">
            <MagnifyingGlassMinus size={18} />
          </button>
          <span className="text-xs text-zinc-400 w-10 text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={onZoomIn} data-testid="zoom-in-btn" className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors">
            <MagnifyingGlassPlus size={18} />
          </button>
        </div>
        <div className="w-px h-5 bg-white/10 mx-1 hidden sm:block" />
        <button
          onClick={onTogglePerformance}
          data-testid="performance-mode-btn"
          title={performanceMode ? "Performance Mode is ON — effects shown simplified for faster editing" : "Turn on Performance Mode to simplify effects while editing"}
          aria-pressed={performanceMode}
          className={`inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg text-xs font-medium transition-colors ${performanceMode ? "bg-amber-500/20 text-amber-400" : "text-zinc-400 hover:bg-white/10 hover:text-white"}`}
        >
          <Lightning size={16} weight={performanceMode ? "fill" : "regular"} />
          <span className="hidden md:inline">Fast</span>
        </button>
      </div>

      <div className="flex items-center gap-2">
        {isPro ? (
          <span data-testid="pro-badge" title="FlyerStudio Pro" className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-amber-400/15 text-amber-300 text-xs font-bold">
            <Crown weight="fill" size={14} /> Pro
          </span>
        ) : (
          <button onClick={onUpgrade} data-testid="topbar-upgrade-btn" title="Upgrade to Pro" className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-amber-400/15 hover:bg-amber-400/25 text-amber-300 text-sm font-semibold transition-colors">
            <Crown weight="fill" size={15} /> <span className="hidden sm:inline">Upgrade</span>
          </button>
        )}
        <button onClick={onResizeClick} data-testid="resize-btn" className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-medium transition-colors">
          <ArrowsOut size={16} /> Resize
        </button>
        <button onClick={onFindReplace} data-testid="find-replace-btn" title="Find & replace text (Ctrl/Cmd+H)" className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-medium transition-colors">
          <MagnifyingGlass size={16} /> Find &amp; replace
        </button>
        <button onClick={onSaveTemplate} data-testid="save-template-btn" title="Save this design as a reusable template" className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-medium transition-colors">
          <FloppyDisk size={16} /> Save as template
        </button>
        <button onClick={onShare} data-testid="share-btn" className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-medium transition-colors">
          <ShareNetwork size={16} /> Share
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="download-btn" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold transition-colors">
              <DownloadSimple weight="bold" size={16} /> Download
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#161616] border-white/10 text-white w-52">
            <DropdownMenuItem onClick={() => onExport("png")} data-testid="export-png" className="cursor-pointer focus:bg-white/10"><FilePng size={16} className="mr-2" /> PNG image</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExport("jpg")} data-testid="export-jpg" className="cursor-pointer focus:bg-white/10"><FileJpg size={16} className="mr-2" /> JPG image</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExport("pdf")} data-testid="export-pdf" className="cursor-pointer focus:bg-white/10 flex items-center justify-between">
              <span className="flex items-center"><FilePdf size={16} className="mr-2" /> PDF document</span>
              {!isPro && <Lock size={12} weight="fill" className="text-amber-300 ml-2" />}
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-white/10" />
            <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">Video <Crown weight="fill" size={10} className="text-amber-300" /></div>
            <DropdownMenuItem onClick={() => onExportVideo("mp4")} data-testid="export-video-mp4" className="cursor-pointer focus:bg-white/10"><FilmSlate size={16} className="mr-2" /> Video (MP4)</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExportVideo("webm")} data-testid="export-video-webm" className="cursor-pointer focus:bg-white/10"><FilmSlate size={16} className="mr-2" /> Video (WebM)</DropdownMenuItem>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem onClick={onShare} data-testid="export-link" className="cursor-pointer focus:bg-white/10"><Link size={16} className="mr-2" /> Share link</DropdownMenuItem>
            <DropdownMenuItem onClick={onSaveTemplate} data-testid="export-save-template" className="cursor-pointer focus:bg-white/10"><FloppyDisk size={16} className="mr-2" /> Save as template</DropdownMenuItem>
            {isAdmin && (
              <DropdownMenuItem onClick={onSaveToStyles} data-testid="export-save-styles" className="cursor-pointer focus:bg-white/10 text-fuchsia-200"><Images size={16} className="mr-2" /> Save to Styles</DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
